import React, { useEffect, useState } from "react";
import { Highlight } from "../../model/Highlight";
import { API_URLS } from "../../App";
import api from "../../hooks/api";
import { ExternalLink, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImageCarousel from "./ImageCarousel";

const HighlightsSection: React.FC = () => {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const response = await api.get(`${API_URLS.HIGHLIGHTS}/active`);
        setHighlights(response.data || []);
      } catch (error) {
        console.log("Error fetching active highlights:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHighlights();
  }, []);

  const handleHighlightClick = (highlight: Highlight) => {
    if (highlight.compound_id) {
      navigate(`/pc/${highlight.compound_id}`);
    } else if (highlight.link_url) {
      window.open(highlight.link_url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className="mb-8 p-6 bg-gray-50 rounded-lg">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (highlights.length === 0) {
    return null; // Don't show anything if no active highlights
  }

  return (
    <div className="overflow-hidden">
      <div
        className="flex justify-between items-center p-4 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-xl font-bold text-gray-900 tracking-tight">
          Destaques
        </span>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600" />
        )}
      </div>

      {expanded && (
        <div className="py-4 border-t border-gray-200">
          <div className="overflow-x-auto">
            <div
              className="flex gap-6 pb-1"
              style={{ minWidth: "min-content" }}
            >
              {highlights.map((highlight) => (
                <div
                  key={highlight.id}
                  onClick={() => handleHighlightClick(highlight)}
                  className={`bg-white rounded-lg shadow-md overflow-hidden transition-all flex-shrink-0 w-80 border border-gray-100 ${
                    highlight.compound_id || highlight.link_url
                      ? "cursor-pointer hover:shadow-lg hover:-translate-y-1"
                      : ""
                  }`}
                >
                  {/* Image Carousel */}
                  {highlight.images && highlight.images.length > 0 && (
                    <ImageCarousel
                      images={highlight.images}
                      alt={highlight.title}
                    />
                  )}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {highlight.title}
                      {highlight.link_url && !highlight.compound_id && (
                        <ExternalLink className="inline w-4 h-4 ml-1 text-blue-600" />
                      )}
                    </h3>
                    {highlight.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                        {highlight.description}
                      </p>
                    )}
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        Válido até{" "}
                        {new Date(highlight.end_date).toLocaleDateString(
                          "pt-PT",
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HighlightsSection;
